/**
 * Conjunto de icones em SVG, no lugar dos emoji que a interface usava antes.
 * Emoji mudam de desenho conforme o sistema operacional e nao aceitam cor, o
 * que deixava a barra de voz diferente em cada maquina.
 *
 * Todos herdam a cor do texto (`currentColor`) e o tamanho do CSS de quem os
 * usa, entao dao para reaproveitar em qualquer botao.
 */
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

/** Base comum: traco de 1.7, pontas arredondadas, sem preenchimento. */
function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export function EyeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  );
}

export function EyeOffIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10.6 6.7A8.9 8.9 0 0 1 12 6.6c6 0 9.5 6.5 9.5 6.5a16 16 0 0 1-2.4 3.2" />
      <path d="M6.4 8.3A16 16 0 0 0 2.5 13s3.5 6.5 9.5 6.5a9 9 0 0 0 3.7-.8" />
      <path d="M9.9 10.4a3 3 0 0 0 4.2 4.2" />
      <path d="m3 3 18 18" />
    </Icon>
  );
}

export function ArrowLeftIcon(props: IconProps) {
  return (
    <Icon {...props} width={16} height={16}>
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </Icon>
  );
}

// ------------------------------------------------------------------- voz

export function MicIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
      <path d="M12 17.5V21" />
    </Icon>
  );
}

export function MicOffIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M15 5v-.5a3 3 0 0 0-6 0V10" />
      <path d="M9 12.8a3 3 0 0 0 5.2.9" />
      <path d="M5.5 11a6.5 6.5 0 0 0 9.9 5.6" />
      <path d="M18.5 11c0 .6-.1 1.2-.2 1.8" />
      <path d="M12 17.5V21" />
      <path d="m3 3 18 18" />
    </Icon>
  );
}

export function VolumeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M11 5 6.5 8.8H3v6.4h3.5L11 19z" />
      <path d="M15.5 9.2a4 4 0 0 1 0 5.6" />
      <path d="M18.2 6.5a8 8 0 0 1 0 11" />
    </Icon>
  );
}

export function VolumeOffIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M11 5 6.5 8.8H3v6.4h3.5L11 19z" />
      <path d="m16 9.5 5 5" />
      <path d="m21 9.5-5 5" />
    </Icon>
  );
}

export function VideoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="6" width="13" height="12" rx="2.5" />
      <path d="m15.5 10.5 6-3v9l-6-3z" />
    </Icon>
  );
}

export function ScreenShareIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="4" width="19" height="12.5" rx="2" />
      <path d="M8.5 20.5h7" />
      <path d="M12 10.2v-3" />
      <path d="m9.8 9.2 2.2-2 2.2 2" />
    </Icon>
  );
}

export function PhoneIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7.7 4.2a1.8 1.8 0 0 0-2.5-.3l-1.4 1a2 2 0 0 0-.6 2.4 18 18 0 0 0 8.6 8.6 2 2 0 0 0 2.4-.6l1-1.4a1.8 1.8 0 0 0-.3-2.5l-1.6-1.2a1.8 1.8 0 0 0-2.1 0l-.8.6a13 13 0 0 1-3.1-3.1l.6-.8a1.8 1.8 0 0 0 0-2.1z" />
    </Icon>
  );
}

export function PhoneOffIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10.6 5.3a12 12 0 0 0-6.4 2.3c-1 .8-1.3 2.2-.6 3.2l1 1.5c.6.9 1.8 1.2 2.7.7l1.5-.8" />
      <path d="M14.4 12.6l1.3 1.6c.6.8 1.8 1 2.7.5l1.6-.9c1-.6 1.4-1.9.8-2.9a12 12 0 0 0-3.4-3.6" />
      <path d="m3 3 18 18" />
    </Icon>
  );
}

// --------------------------------------------------------------- interface

export function SettingsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5v.2a2 2 0 0 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1h.2a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
    </Icon>
  );
}

export function LogOutIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 21H5.5A1.5 1.5 0 0 1 4 19.5v-15A1.5 1.5 0 0 1 5.5 3H9" />
      <path d="m16 16 4-4-4-4" />
      <path d="M20 12H9" />
    </Icon>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </Icon>
  );
}

export function PencilIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 20h4l10.5-10.5a2.8 2.8 0 0 0-4-4L4 16z" />
      <path d="m14.5 5.5 4 4" />
    </Icon>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m6 6 12 12" />
      <path d="m18 6-12 12" />
    </Icon>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </Icon>
  );
}

export function SendIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m4.5 12 15.5-7.5-4 15.5-4-6z" />
      <path d="m11.9 14 8.1-9.5" />
    </Icon>
  );
}

export function MailIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </Icon>
  );
}

export function ShieldIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2.7 4.5 5.8v5.6c0 4.4 3.1 8.5 7.5 9.9 4.4-1.4 7.5-5.5 7.5-9.9V5.8z" />
    </Icon>
  );
}

export function UsersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="9" cy="8" r="3.3" />
      <path d="M2.8 20a6.2 6.2 0 0 1 12.4 0" />
      <path d="M16.2 5.2a3.3 3.3 0 0 1 0 5.6" />
      <path d="M18 14.4a6.2 6.2 0 0 1 3.2 5.6" />
    </Icon>
  );
}

export function TrashIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 6.5h16" />
      <path d="M9.5 6.5V4.8a1.3 1.3 0 0 1 1.3-1.3h2.4a1.3 1.3 0 0 1 1.3 1.3v1.7" />
      <path d="M6.2 6.5 7 19.2a1.8 1.8 0 0 0 1.8 1.7h6.4a1.8 1.8 0 0 0 1.8-1.7l.8-12.7" />
    </Icon>
  );
}

/** "Puxar para o meu canal": a seta entra na chave. */
export function PullIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 12h10" />
      <path d="m10.5 8.5 3.5 3.5-3.5 3.5" />
      <path d="M17 4h1.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H17" />
    </Icon>
  );
}
